import { Form, Formik, useFormikContext } from "formik";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { createHourAsync, updateHourAsync } from "../../../slices/hour";
import { PMField } from "../../utils/PMField";
import { PMDatePicker } from "../../utils/PMDatePicker";
import { fetchHour, fetchHourRegistrationPolicy, fetchHours } from "../../../api/hour";
import { uploadModuleDocuments } from "../../../api/moduleDocumentUpload";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { ResourceLoading } from "../../utils/ResourceLoading";
import CarAllowanceSelectionModal from "../driving_record/carAllowanceSelectionModal";
import PropTypes from "prop-types";
import { dispatchWithToastV2 } from "../../Utils";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { ModernProjectActivitySelectorField } from "../../utils/moduleSelectors/ModernProjectActivitySelectorField";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { ReportSelector } from "../../utils/moduleSelectors/reportSelector";
import { useParams } from "react-router-dom";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { fetchDrivingTrips } from "../../../api/driving/trip";
import { ApproveHourDialog } from "./approveHourDialog";
import { HistoryModuleView } from "../histories/module-view";
import axios from "../../../api/axios";
import { checkAbsenceForDate } from "../../../api/absence/absenece";
import { PMNotice } from "../../utils/notifications";
import { DocumentManagerV6 } from "../Media/DocumentManagerV6";

// Helper functions to convert between decimal hours and hours/minutes
const decimalToHoursMinutes = (decimalHours) => {
  if (!decimalHours && decimalHours !== 0) return { hours: 0, minutes: 0 };
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return { hours, minutes };
};

const hoursMinutesToDecimal = (hours, minutes) => {
  const h = parseFloat(hours) || 0;
  const m = parseFloat(minutes) || 0;
  return h + (m / 60);
};

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = String(dateStr).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const hourLinkedToReport = (reportId) => {
  if (reportId === null || reportId === undefined || reportId === "") return false;
  const n = Number(reportId);
  if (Number.isNaN(n)) return true;
  return n !== 0;
};

const HOUR_PENDING_DOC_MIME_WHITELIST = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/svg",
  "image/svg+xml",
  "image/webp",
  "application/pdf",
]);

const pendingDocAcceptAttr =
  "image/png, image/jpeg, image/jpg, image/gif, image/svg, image/svg+xml, image/webp, application/pdf";

function isHourPendingDocMimeAllowed(file) {
  const t = (file?.type || "").toLowerCase().trim();
  return t && HOUR_PENDING_DOC_MIME_WHITELIST.has(t);
}

/** RTK `dispatchWithToastV2` passes the fulfilled action.payload; unwrap nested shapes if present. */
function normalizeHourThunkPayload(savedPayload) {
  if (!savedPayload || typeof savedPayload !== "object") return savedPayload;
  if (savedPayload.data && typeof savedPayload.data === "object" && savedPayload.data.id != null) {
    return savedPayload.data;
  }
  if (savedPayload.hour && typeof savedPayload.hour === "object" && savedPayload.hour.id != null) {
    return savedPayload.hour;
  }
  return savedPayload;
}

function coercePositiveHourDbId(raw) {
  if (raw == null || raw === "") return null;
  const n =
    typeof raw === "number" && Number.isFinite(raw)
      ? Math.trunc(raw)
      : parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

// Observe form date changes and notify parent + internal state
const FormikDateObserver = ({ onDateChange, onInternalDate }) => {
  const { values } = useFormikContext();
  const prevRef = useRef(values?.date);
  useEffect(() => {
    const d = values?.date;
    if (d && d !== prevRef.current) {
      prevRef.current = d;
      if (onDateChange) onDateChange(d);
      if (onInternalDate) onInternalDate(d);
    }
  }, [values?.date]);
  return null;
};

// Observe hours/minutes changes and sync with time field
// Also observe time field changes and sync back to hours/minutes
// Only active when using hours_minutes format
const HoursMinutesObserver = ({ useHoursMinutesFormat }) => {
  const { values, setFieldValue } = useFormikContext();
  const prevHoursRef = useRef(values?.hours);
  const prevMinutesRef = useRef(values?.minutes);
  const prevTimeRef = useRef(values?.time);
  
  // Sync hours/minutes -> time (only when both are valid integers, not during decimal input)
  useEffect(() => {
    // Only observe if using hours_minutes format
    if (!useHoursMinutesFormat) {
      return;
    }
    
    const hoursStr = values?.hours;
    const minutesStr = values?.minutes;
    
    // Check if hours contains a decimal point (user typing 2.5 or 2,5)
    const hoursIsDecimal = hoursStr && (hoursStr.toString().includes('.') || hoursStr.toString().includes(','));
    
    // Only sync if hours is NOT a decimal (i.e., user is typing integer hours)
    if (!hoursIsDecimal && (hoursStr !== prevHoursRef.current || minutesStr !== prevMinutesRef.current) && 
        (hoursStr !== undefined && minutesStr !== undefined)) {
      const hoursNum = parseFloat(hoursStr?.toString().replace(',', '.') || '0');
      const minutesNum = parseFloat(minutesStr?.toString().replace(',', '.') || '0');
      
      prevHoursRef.current = hoursStr;
      prevMinutesRef.current = minutesStr;
      
      // Use integer values for calculation
      const hoursInt = Math.floor(hoursNum);
      const minutesInt = Math.round(minutesNum);
      const decimalTime = hoursMinutesToDecimal(hoursInt, minutesInt);
      const timeStr = decimalTime.toFixed(3);
      if (timeStr !== prevTimeRef.current) {
        prevTimeRef.current = timeStr;
        setFieldValue("time", timeStr, false); // false = don't validate
      }
    } else if (hoursIsDecimal) {
      // If user is typing a decimal, just update the time field without converting hours/minutes
      const hoursNum = parseFloat(hoursStr?.toString().replace(',', '.') || '0');
      if (!isNaN(hoursNum) && hoursNum >= 0) {
        const timeStr = hoursNum.toFixed(3);
        if (timeStr !== prevTimeRef.current) {
          prevTimeRef.current = timeStr;
          setFieldValue("time", timeStr, false);
        }
      }
    }
  }, [values?.hours, values?.minutes, setFieldValue, useHoursMinutesFormat]);
  
  // Sync time -> hours/minutes (when time is updated externally, but not if hours is currently a decimal)
  useEffect(() => {
    // Only observe if using hours_minutes format
    if (!useHoursMinutesFormat) {
      return;
    }
    
    const timeValue = values?.time;
    const hoursStr = values?.hours;
    const hoursIsDecimal = hoursStr && (hoursStr.toString().includes('.') || hoursStr.toString().includes(','));
    
    // Only sync back if time changed externally AND user is not currently typing a decimal
    if (!hoursIsDecimal && timeValue !== undefined && timeValue !== prevTimeRef.current && timeValue !== "") {
      prevTimeRef.current = timeValue;
      const decimalTime = parseFloat(timeValue);
      if (!isNaN(decimalTime)) {
        const { hours, minutes } = decimalToHoursMinutes(decimalTime);
        const hoursStr = hours.toString();
        const minutesStr = minutes.toString();
        if (hoursStr !== prevHoursRef.current || minutesStr !== prevMinutesRef.current) {
          prevHoursRef.current = hoursStr;
          prevMinutesRef.current = minutesStr;
          setFieldValue("hours", hoursStr, false);
          setFieldValue("minutes", minutesStr, false);
        }
      }
    }
  }, [values?.time, values?.hours, setFieldValue, useHoursMinutesFormat]);
  
  return null;
};


export const HourForm = ({ hourId, onChange, prefill = null, onDateChange = null, renderAfterDateTime = null, renderAfterDescription = null, onClose = null, showQuickHourSelection = false, hideProjectSelector = false, onWarningStateChange = null }) => {
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [resolvedId, setResolvedId] = useState(null);
  const currentUser = useSelector((state) => state.session.currentUser);
  // Get time registration format from user settings, default to "hours_minutes"
  const timeRegistrationFormat = currentUser?.time_registration_format || "hours_minutes";
  const useHoursMinutesFormat = timeRegistrationFormat === "hours_minutes";

  const [item, setItem] = useState({});
  const isAdmin = useUserHasAccess({ role: "hours", action: "admin" });
  const canCarAllowance = useUserHasAccess({ role: "driving_trips" });
  /** Matches API: only company admin may process hours (hour[process]=true) */
  const canProcessHour = useMemo(
    () => Boolean(currentUser?.roles?.some((r) => r.name === "admin")),
    [currentUser?.roles]
  );
  const [hasTripsAvailable, setHasTripsAvailable] = useState(false);
  const [formDate, setFormDate] = useState(prefill?.date || null);
  const [datePickerDayInfo, setDatePickerDayInfo] = useState({});
  const [registrationPolicy, setRegistrationPolicy] = useState(null);
  const [pendingHourDocuments, setPendingHourDocuments] = useState([]);
  const pendingHourDocInputRef = useRef(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [warningData, setWarningData] = useState(null);
  const [noticeErrors, setNoticeErrors] = useState(null);
  /** "details" | "history" — history as tab avoids stacking/clipping inside PMDialog */
  const [hourEditorTab, setHourEditorTab] = useState("details");

  // Update resolvedId whenever hourId or id changes
  // Prioritize hourId prop over id from useParams
  useEffect(() => {
    if (hourId !== undefined) {
      // hourId is explicitly provided (even if null)
      setResolvedId(hourId || null);
    } else {
      // hourId not provided, fall back to id from route
      setResolvedId(id || null);
    }
  }, [hourId, id]);

  useEffect(() => {
    setHourEditorTab("details");
  }, [resolvedId]);

  const isEditingExistingHour = Boolean(resolvedId);

  /** Prefilled AO/report/project context → hide selectors for normal users; admin may override */
  const { showProjectField, showActivityField } = useMemo(() => {
    if (isEditingExistingHour) {
      return { showProjectField: true, showActivityField: true };
    }
    if (!prefill || typeof prefill !== "object") {
      return { showProjectField: true, showActivityField: true };
    }
    const nonEmptyId = (v) =>
      v != null && String(v).trim() !== "";
    let lockProj = false;
    if (
      nonEmptyId(prefill.report_id) ||
      nonEmptyId(prefill.work_order_id) ||
      nonEmptyId(prefill.project_id) ||
      prefill.project?.id != null
    ) {
      lockProj = true;
    }
    let lockAct = false;
    if (
      nonEmptyId(prefill.project_activity_id) ||
      prefill.project_activity?.id != null
    ) {
      lockAct = true;
    }
    const canReveal =
      isAdmin || canProcessHour;
    return {
      showProjectField: !lockProj || canReveal,
      showActivityField: !lockAct || canReveal,
    };
  }, [isEditingExistingHour, prefill, isAdmin, canProcessHour]);

  const showProjectSelectorUi = showProjectField && !hideProjectSelector;
  const showProjectActivityUi = showActivityField;

  const createOrUpdateDispatch = (data) => {
    if (item?.id) {
      const res = dispatch(updateHourAsync(data));
      return res;
    } else {
      const res = dispatch(createHourAsync(data));
      return res;
    }
  };
  
  const submitHour = (values, helpers) => {
    try {
      // Clear previous notice errors
      setNoticeErrors(null);

      // Remove hours and minutes from payload - only send time (decimal format) to backend
      const { hours, minutes, project_activity, activity, ...payload } = values;
      dispatchWithToastV2(createOrUpdateDispatch(payload), {
        onRefresh: (thunkSavedPayload) => {
          const savedHour = normalizeHourThunkPayload(thunkSavedPayload);
          const hourNumericId = coercePositiveHourDbId(savedHour?.id ?? thunkSavedPayload?.id);

          const completeHourSaveSuccess = () => {
            if (onChange) {
              onChange(savedHour);
            }
            setLoading(false);
            setNoticeErrors(null); // Clear errors on success
            handleEmptyForm(helpers);
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
            if (onClose) {
              onClose();
            }
          };

          if (pendingHourDocuments.length > 0) {
            if (!hourNumericId) {
              console.error("[HourForm] Pending documents but missing hour id in save response", thunkSavedPayload);
              toast.error(t("hour.time_documents.pending_upload_failed"));
              completeHourSaveSuccess();
              return;
            }
            const filesToUpload = pendingHourDocuments.map((p) => p.file);
            setLoading(true);
            uploadModuleDocuments("hour", hourNumericId, filesToUpload)
              .then(() => {
                setPendingHourDocuments([]);
                toast.success(
                  t("hour.time_documents.pending_upload_done", { count: filesToUpload.length })
                );
              })
              .catch((err) => {
                console.error(err);
                toast.error(t("hour.time_documents.pending_upload_failed"));
              })
              .finally(() => {
                completeHourSaveSuccess();
              });
            return;
          }

          completeHourSaveSuccess();
        },
        onError: (errorPayload) => {
          setLoading(false);
          
          // Handle notice messages from backend - check multiple possible locations
          let errors = null;
          
          // Check if errors are directly in errorPayload (from success callback with errors)
          if (errorPayload?.errors && typeof errorPayload.errors === "object") {
            errors = errorPayload.errors;
          }
          // Check if errors are in errorPayload.response.data.errors (from axios error in error callback)
          else if (errorPayload?.response?.data?.errors && typeof errorPayload.response.data.errors === "object") {
            errors = errorPayload.response.data.errors;
          }
          // Check if errorPayload.response.data itself is the errors object
          else if (errorPayload?.response?.data && typeof errorPayload.response.data === "object") {
            const data = errorPayload.response.data;
            // Check if it looks like an errors object (has field names as keys with array/string values)
            const keys = Object.keys(data);
            if (keys.length > 0 && keys.every(key => 
              Array.isArray(data[key]) || typeof data[key] === "string"
            )) {
              errors = data;
            }
          }
          // Check if errorPayload itself is the errors object (direct from setErrors)
          else if (errorPayload && typeof errorPayload === "object" && !errorPayload.response && !errorPayload.message) {
            // Check if it looks like an errors object (has field names as keys)
            const keys = Object.keys(errorPayload);
            if (keys.length > 0 && keys.every(key => 
              Array.isArray(errorPayload[key]) || typeof errorPayload[key] === "string"
            )) {
              errors = errorPayload;
            }
          }
          
          if (errors) {
            const fieldErrors = {};
            const noticeMessages = [];
            
            Object.entries(errors).forEach(([field, messages]) => {
              if (Array.isArray(messages) && messages.length > 0) {
                // Use the first message for the field
                const errorMessage = messages[0];
                fieldErrors[field] = errorMessage;
                // Collect notice messages for display
                noticeMessages.push({
                  field: field,
                  message: errorMessage
                });
              } else if (typeof messages === "string") {
                fieldErrors[field] = messages;
                noticeMessages.push({
                  field: field,
                  message: messages
                });
              }
            });
            
            // Set Formik field errors first
            if (Object.keys(fieldErrors).length > 0) {
              helpers.setErrors(fieldErrors);
            }
            
            // Store notice errors for display
            if (noticeMessages.length > 0) {
              setNoticeErrors(noticeMessages);
            }
          }
          
          helpers.setStatus({ success: false });
          helpers.setSubmitting(false);
          // Do NOT close dialog on error - let user see the errors
        },
        setErrors: (errors) => {
          // Handle errors from dispatchWithToastV2
          // This is called before onError, so we handle errors here first
          if (errors && typeof errors === "object") {
            const fieldErrors = {};
            const noticeMessages = [];
            
            Object.entries(errors).forEach(([field, messages]) => {
              if (Array.isArray(messages) && messages.length > 0) {
                // Use the first message for the field
                const errorMessage = messages[0];
                fieldErrors[field] = errorMessage;
                noticeMessages.push({
                  field: field,
                  message: errorMessage
                });
              } else if (typeof messages === "string") {
                fieldErrors[field] = messages;
                noticeMessages.push({
                  field: field,
                  message: messages
                });
              }
            });
            
            // Set Formik field errors
            if (Object.keys(fieldErrors).length > 0) {
              helpers.setErrors(fieldErrors);
            }
            
            // Store notice errors for display
            if (noticeMessages.length > 0) {
              setNoticeErrors(noticeMessages);
            }
            
            // Also update loading and submitting state
            setLoading(false);
            helpers.setStatus({ success: false });
            helpers.setSubmitting(false);
          }
        },
      });
    } catch (err) {
      setLoading(false);
      toast.error("Something went wrong...");
      console.error(err);
      helpers.setStatus({ success: false });
      helpers.setSubmitting(false);
      // Do NOT close dialog on error
    }
  };
  
  const handleWarningConfirm = () => {
    if (pendingSubmission) {
      submitHour(pendingSubmission.values, pendingSubmission.helpers);
      setPendingSubmission(null);
      setWarningData(null);
      setWarningDialogOpen(false);
    }
  };
  
  // Expose methods to parent for warning dialog control
  useEffect(() => {
    if (typeof onWarningStateChange === 'function') {
      onWarningStateChange({
        warningDialogOpen,
        warningData,
        pendingSubmission,
        setWarningDialogOpen,
        setWarningData,
        setPendingSubmission,
        handleWarningConfirm,
      });
    }
  }, [warningDialogOpen, warningData, pendingSubmission, onWarningStateChange]);

  const getItem = async (itemId) => {
    setLoading(true);
    fetchHour(itemId)
      .then((res) => {
        const d = res.data;
        if (d && typeof d === "object") {
          const { activity: _legacyActivity, ...rest } = d;
          setItem(rest);
        } else {
          setItem(d);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  // Fetch item when resolvedId changes
  useEffect(() => {
    if (resolvedId) {
      getItem(resolvedId);
      setNoticeErrors(null); // Clear notice errors when loading a new hour
    } else {
      // Clear item state when creating new hour
      setItem({});
      setLoading(false);
      setNoticeErrors(null); // Clear notice errors when creating new hour
    }
  }, [resolvedId]);

  useEffect(() => {
    let active = true;
    fetchHourRegistrationPolicy()
      .then((res) => {
        if (!active) return;
        setRegistrationPolicy(res?.data?.data || null);
      })
      .catch(() => {
        if (active) setRegistrationPolicy(null);
      });
    return () => {
      active = false;
    };
  }, []);

  // Clear item when hourId becomes null (switching to new hour)
  useEffect(() => {
    // If hourId is explicitly null, clear the form
    if (hourId === null) {
      setItem({});
      setResolvedId(null);
      setPendingHourDocuments([]);
      // Reset formDate to prefill date or null
      setFormDate(prefill?.date || null);
      setNoticeErrors(null); // Clear notice errors when switching to new hour
    }
  }, [hourId, prefill?.date]);

  // Check trips availability for car allowance when date or user changes
  useEffect(() => {
    let active = true;
    async function checkTrips() {
      if (!formDate || !currentUser?.id) {
        if (active) setHasTripsAvailable(false);
        return;
      }
      try {
        const res = await fetchDrivingTrips({
          filters: [
            { name: "USER", value: currentUser.id },
            { name: "DATE", value: formDate },
          ],
          per_page: 1,
          page: 1,
        });
        const list = Array.isArray(res?.data) ? res.data : [];
        if (active) setHasTripsAvailable(list.length > 0);
      } catch (e) {
        if (active) setHasTripsAvailable(false);
      }
    }
    checkTrips();
    return () => {
      active = false;
    };
  }, [formDate, currentUser?.id]);

  // Fetch hours per day for date picker (aggregate by date for dayInfoMap)
  const fetchHoursForMonth = async (month, year, userId) => {
    const uid = userId || currentUser?.id;
    if (!uid) return {};
    const lastDay = new Date(year, month, 0).getDate();
    const dateFrom = `${year}-${String(month).padStart(2, "0")}-01`;
    const dateTo = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    try {
      const res = await fetchHours({
        date_from: dateFrom,
        date_to: dateTo,
        per_page: 200,
        page: 1,
        filters: [{ name: "user", value: uid }],
      });
      const hours = res?.data || [];
      const byDate = {};
      hours.forEach((h) => {
        const d = h?.date;
        if (d) {
          byDate[d] = (byDate[d] || 0) + (parseFloat(h?.time) || 0);
        }
      });
      const dayInfoMap = {};
      Object.entries(byDate).forEach(([date, total]) => {
        const h = Math.floor(total);
        const m = Math.round((total - h) * 60);
        dayInfoMap[date] = m > 0 ? `${h}t ${m}m` : `${h}t`;
      });
      const daysInMonth = new Date(year, month, 0).getDate();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        if (isLockedByPolicy(date)) {
          dayInfoMap[date] = dayInfoMap[date] ? `${dayInfoMap[date]} | Låst` : "Låst";
        }
      }
      return dayInfoMap;
    } catch (e) {
      console.error("Error fetching hours for date picker:", e);
      return {};
    }
  };

  const isLockedByPolicy = (dateValue) => {
    if (canProcessHour) return false;
    if (!registrationPolicy?.payroll_lock_enabled) return false;

    const date = parseLocalDate(dateValue);
    if (!date) return false;

    const lockHour = Number.parseInt(registrationPolicy?.payroll_lock_hour, 10);
    const effectiveLockHour = Number.isNaN(lockHour) ? 18 : Math.min(23, Math.max(0, lockHour));
    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, effectiveLockHour, 0, 0, 0);

    return new Date() >= monthEnd;
  };

  // useEffect(() => {
  //   if (selected) {
  //     // Fetch hour and update state
  //     getItem(selected.id);
  //   } else {
  //     setLoading(false);
  //   }
  // }, [selected]);

  const handleEmptyForm = (formik) => {
    setLoading(true);
    if (formik) {
      formik.resetForm();
    }
    setItem({});
    setResolvedId(null);
    setPendingHourDocuments([]);
    setNoticeErrors(null); // Clear notice errors when resetting form

    // remove only :id from the URL
    const newPath = window.location.pathname.replace(/\/\d+$/, "");
    window.history.pushState({}, "", newPath);

    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const [carAllowanceDialogOpen, setCarAllowanceDialogOpen] = useState(false);

  const formDisabled = (dateValue = null) => {
    if (item?.id && item?.approved && !canProcessHour) {
      return true;
    }
    if (isLockedByPolicy(dateValue || item?.date || formDate || prefill?.date)) {
      return true;
    }
    return false;
  };

  if (loading) return <ResourceLoading />;

  return (
    <>
      <Formik
        key={hourId || `new-${prefill?.report_id || prefill?.project_id || 'default'}`}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          id: item?.id || null,
          user_id: item?.id 
            ? (item?.user?.id || currentUser?.id)
            : (prefill?.user_id || currentUser?.id),
          project_id: item?.id
            ? (item?.project?.id ? item.project.id.toString() : null)
            : (prefill?.project_id != null
              ? prefill.project_id.toString()
              : prefill?.project?.id != null
                ? prefill.project.id.toString()
                : null),
          project_activity_id: item?.id
            ? item?.project_activity?.id != null
              ? String(item.project_activity.id)
              : item?.project_activity_id != null
                ? String(item.project_activity_id)
                : null
            : prefill?.project_activity_id != null
              ? String(prefill.project_activity_id)
              : null,
          project_activity: item?.id
            ? item?.project_activity ||
                (item?.project_activity_id != null ? { id: item.project_activity_id } : null)
            : prefill?.project_activity || null,
          time: item?.id
            ? (item?.time || "")
            : (prefill?.time || ""),
          // Convert time to hours and minutes for display (only if using hours_minutes format)
          ...(useHoursMinutesFormat ? (() => {
            const timeValue = item?.id
              ? (item?.time || "")
              : (prefill?.time || "");
            const decimalTime = parseFloat(timeValue) || 0;
            const { hours, minutes } = decimalToHoursMinutes(decimalTime);
            return { hours: hours.toString(), minutes: minutes.toString() };
          })() : { hours: "0", minutes: "0" }),
          date: item?.id
            ? (item?.date || new Date().toISOString().substring(0, 10))
            : (prefill?.date || new Date().toISOString().substring(0, 10)),
          report_id: item?.id
            ? (item?.report?.id || null)
            : (prefill?.report_id || null),
          work_order_id: item?.id
            ? (item?.work_order?.id || null)
            : (prefill?.work_order_id || null),
          description: item?.id
            ? (item?.description || "")
            : (prefill?.description || ""),
          submit: null,
        }}
        validationSchema={Yup.object({
          date: Yup.date().required("Date is required!"),
          time: Yup.string().required("Time is required!"),
          // Only validate hours/minutes if using hours_minutes format
          ...(useHoursMinutesFormat ? {
            hours: Yup.string()
              .test('is-valid-number', 'Hours must be a valid number', (value) => {
                if (!value || value === '') return false;
                const num = parseFloat(value.replace(',', '.'));
                return !isNaN(num) && num >= 0;
              })
              .required("Hours is required!"),
            minutes: Yup.string()
              .test('is-valid-number', 'Minutes must be a valid number between 0 and 59', (value) => {
                if (!value || value === '') return false;
                const num = parseFloat(value.replace(',', '.'));
                return !isNaN(num) && num >= 0 && num <= 59;
              })
              .required("Minutes is required!"),
          } : {}),
          project_id: Yup.string()
            .nullable()
            .required("Project is required!"),
          // report_id: Yup.string().nullable().required("Report is required!"),
          // deviation_id: Yup.string().nullable().required("Deviation is required!"),
          project_activity_id: Yup.string()
            .nullable()
            .required("Aktivitet er påkrevd"),
        })}
        onSubmit={async (values, helpers) => {
          setLoading(true);
          try {
            if (isLockedByPolicy(values.date)) {
              setLoading(false);
              helpers.setSubmitting(false);
              setNoticeErrors([{ field: "date", message: t("Denne datoen er låst for timeregistrering.") }]);
              return;
            }

            // Check if there's an absence for this date
            if (values.user_id && values.date) {
              try {
                const hasAbsence = await checkAbsenceForDate(values.user_id, values.date);
                if (hasAbsence) {
                  setLoading(false);
                  toast.error(t("Cannot register hours on a day with absence registered"));
                  helpers.setSubmitting(false);
                  return;
                }
              } catch (err) {
                console.error("Error checking absence:", err);
                // Continue with submission if check fails
              }
            }

            // Check if we need to show warning for price line activity
            if (values.report_id && values.project_activity_id && values.time) {
              try {
                const reportResponse = await axios.get(`/service/reports/${values.report_id}`, {
                  params: { fields: "price_lines.activity_item,price_lines.project_activity" },
                });
                const report = reportResponse.data;

                const paRow = values.project_activity;
                const matchesAgreedLine = (pl) => {
                  if (!pl?.is_agreed_price_line) return false;
                  if (
                    values.project_activity_id &&
                    pl.project_activity_id != null &&
                    String(pl.project_activity_id) === String(values.project_activity_id)
                  ) {
                    return true;
                  }
                  if (
                    paRow?.activity_item_id &&
                    pl.activity_item_id != null &&
                    String(pl.activity_item_id) === String(paRow.activity_item_id)
                  ) {
                    return true;
                  }
                  return false;
                };

                const priceLine = report?.price_lines?.find(matchesAgreedLine);

                if (priceLine && priceLine.agreed_hours > 0) {
                  const hoursResponse = await axios.get(`/hours`, {
                    params: {
                      filters: JSON.stringify([
                        { name: "REPORT", value: values.report_id },
                        { name: "PROJECT_ACTIVITY", value: values.project_activity_id },
                      ]),
                    },
                  });
                  
                  const existingHours = hoursResponse.data?.data || [];
                  const currentTotalHours = existingHours.reduce((sum, h) => sum + (parseFloat(h.time) || 0), 0);
                  const newTime = parseFloat(values.time) || 0;
                  const totalAfterRegistration = currentTotalHours + newTime;
                  
                  // Check if total will exceed estimated hours
                  if (totalAfterRegistration > priceLine.agreed_hours) {
                    // Show warning dialog
                    setLoading(false);
                    setWarningData({
                      activityTitle:
                        priceLine.project_activity?.display_title ||
                        priceLine.project_activity?.title ||
                        priceLine.activity_item?.displayTitle ||
                        priceLine.title ||
                        t("hours.registration_warning.this_activity"),
                      estimatedHours: priceLine.agreed_hours,
                      registeredHours: currentTotalHours,
                      newHours: newTime,
                      totalAfterRegistration: totalAfterRegistration,
                    });
                    setPendingSubmission({ values, helpers });
                    setWarningDialogOpen(true);
                    helpers.setSubmitting(false);
                    return;
                  }
                }
              } catch (err) {
                console.error("Error checking price line:", err);
                // Continue with normal submission if check fails
              }
            }
            
            // Normal submission (no warning needed)
            submitHour(values, helpers);
          } catch (err) {
            setLoading(false);
            toast.error("Something went wrong...");
            console.error(err);
            helpers.setStatus({ success: false });
          }
        }}
      >
        {(formik) => {
          if (loading) {
            return <ResourceLoading />;
          }

          const selectedDateLocked = isLockedByPolicy(formik.values.date);

          // Helper to get current project object
          const getCurrentProject = () => {
            return item?.project || prefill?.project || null;
          };

          const getCurrentProjectActivity = () => {
            return (
              formik.values?.project_activity ||
              item?.project_activity ||
              prefill?.project_activity ||
              null
            );
          };

          const getCurrentEmployee = () => {
            const uid = formik.values.user_id;
            if (uid == null || uid === "") return null;
            if (item?.user && String(item.user.id) === String(uid)) return item.user;
            if (prefill?.user && String(prefill.user.id) === String(uid)) return prefill.user;
            if (currentUser && String(currentUser.id) === String(uid)) return currentUser;
            return { id: uid };
          };

          // Handle submit button click - validate first; only set loading in onSubmit when validation passes
          const handleSubmitClickInternal = async () => {
            if (selectedDateLocked) {
              toast.error(t("Denne datoen er låst for timeregistrering."));
              return;
            }
            if (formDisabled(formik.values.date) || loading) return;
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
              formik.setErrors(errors);
              formik.setTouched(
                Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), { ...formik.touched })
              );
              if (errors.project_activity_id) {
                toast.error(t("Aktivitet er påkrevd"));
              }
              return;
            }
            formik.submitForm();
          };

          return (
            <>
            {item?.id && (
              <div
                className="mb-3 flex gap-1 border-b border-slate-200"
                role="tablist"
                aria-label={`${t("hours.form_tab_details")} / ${t("histories.title")}`}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={hourEditorTab === "details"}
                  className={
                    hourEditorTab === "details"
                      ? "rounded-t-lg border border-b-0 border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-900"
                      : "rounded-t-lg border border-transparent px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  }
                  onClick={() => setHourEditorTab("details")}
                >
                  {t("hours.form_tab_details")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={hourEditorTab === "history"}
                  className={
                    hourEditorTab === "history"
                      ? "rounded-t-lg border border-b-0 border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-900"
                      : "rounded-t-lg border border-transparent px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  }
                  onClick={() => setHourEditorTab("history")}
                >
                  {t("histories.title")}
                </button>
              </div>
            )}

            {hourEditorTab === "history" && item?.id ? (
              <div className="mb-20 max-h-[min(70vh,640px)] overflow-y-auto pr-1">
                <HistoryModuleView variant="embedded" module="hour" module_param={item.id} />
              </div>
            ) : (
            <Form className="mb-20">
              <FormikDateObserver onDateChange={onDateChange} onInternalDate={setFormDate} />
              <HoursMinutesObserver useHoursMinutesFormat={useHoursMinutesFormat} />

              {selectedDateLocked && (
                <div className="mb-4">
                  <PMNotice
                    severity="warning"
                    title={t("Låst lønnsperiode")}
                    description={t("Valgt dato tilhører en låst lønnsperiode og kan ikke redigeres her.")}
                  />
                </div>
              )}

              {item?.id && item?.approved && canProcessHour && (
                <div className="mb-4">
                  <PMNotice
                    severity="info"
                    title={t("hours.approved_edit_notice_title")}
                    description={t("hours.approved_edit_notice_body")}
                  />
                </div>
              )}
              
              {/* Display notice errors from backend */}
              {noticeErrors && noticeErrors.length > 0 && (
                <div className="mb-4">
                  <PMNotice
                    severity="error"
                    title={t("Validation errors")}
                    description={
                      <ul className="list-disc list-inside space-y-1 mt-2">
                        {noticeErrors.map((error, index) => {
                          // Map field names to user-friendly labels
                          const fieldLabels = {
                            date: t("Date"),
                            time: t("Time"),
                            hours: t("Timer"),
                            minutes: t("Minutter"),
                            project_id: t("Prosjekt"),
                            project_activity_id: t("Aktivitet"),
                            user_id: t("Ansatt"),
                            description: t("Beskrivelse"),
                          };
                          const fieldLabel = fieldLabels[error.field] || error.field;
                          return (
                            <li key={index} className="text-sm">
                              <strong>{fieldLabel}:</strong> {t(error.message)}
                            </li>
                          );
                        })}
                      </ul>
                    }
                  />
                </div>
              )}
              
              <div className="flex justify-between">
                <div>
                  {item?.id && (
                    <button
                      type="button"
                      className="inline-flex align-right justify-center rounded-md border border-transparent bg-blue-400 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                      onClick={() => handleEmptyForm(formik)}
                    >
                      {t("New Hour")}
                    </button>
                  )}
                  {item?.id && canProcessHour && (
                    <button
                      type="button"
                      className="ml-2 inline-flex align-right justify-center rounded-md border border-transparent bg-green-400 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                      onClick={() => setApproveDialogOpen(true)} 
                    >
                      {t("Behandle time")}
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6 mt-5">
                {isAdmin && (
                  <div className="col-span-3 sm:col-span-3">
                    <EmployeeSelector
                      name="user_id"
                      label="Ansatt"
                      selected={getCurrentEmployee()}
                      setFieldValue={formik.setFieldValue}
                      values={formik.values}
                      errors={formik.errors}
                      disabled={formDisabled(formik.values.date)}
                      onChange={(e) => {
                        if (e.target.value) {
                          setItem((prev) => ({ ...prev, user: e.target.object }));
                        } else {
                          setItem((prev) => ({ ...prev, user: null }));
                        }
                      }}
                    />
                  </div>
                )}

                {/* Quick Hour Selection */}
                {showQuickHourSelection && !item?.id && (
                  <div className="col-span-3 sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("Rask timeregistrering")}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8].map((decimalHours) => {
                        const { hours, minutes } = decimalToHoursMinutes(decimalHours);
                        return (
                          <button
                            key={decimalHours}
                            type="button"
                            onClick={() => {
                              formik.setFieldValue("hours", hours.toString());
                              formik.setFieldValue("minutes", minutes.toString());
                              formik.setFieldValue("time", decimalHours.toFixed(3));
                            }}
                            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                              parseFloat(formik.values.time) === decimalHours
                                ? "bg-blue-600 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {decimalHours.toFixed(1)}h
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Date first - full row on mobile/phone, inline on larger screens */}
                <div className={useHoursMinutesFormat ? "col-span-3 sm:col-span-1" : "col-span-3 sm:col-span-2"}>
                  <PMDatePicker
                    value={formik.values.date}
                    onChange={(e) => formik.setFieldValue("date", e?.target?.value)}
                    name="date"
                    label={t("Date")}
                    errors={formik.errors.date}
                    touched={formik.touched.date}
                    disabled={formDisabled(formik.values.date)}
                    dayInfoMap={datePickerDayInfo}
                    validationRules={{
                      isDateValid: canProcessHour
                        ? undefined
                        : (date) => !isLockedByPolicy(date),
                    }}
                    onOpen={() => {
                      const d = formik.values.date ? new Date(formik.values.date) : new Date();
                      const month = d.getMonth() + 1;
                      const year = d.getFullYear();
                      const uid = formik.values.user_id || currentUser?.id;
                      if (uid) {
                        fetchHoursForMonth(month, year, uid).then(setDatePickerDayInfo);
                      }
                    }}
                    onMonthChange={(month, year) => {
                      const uid = formik.values.user_id || currentUser?.id;
                      if (uid) {
                        fetchHoursForMonth(month, year, uid).then(setDatePickerDayInfo);
                      }
                    }}
                  />
                </div>
                {useHoursMinutesFormat ? (
                  <>
                    <div className="col-span-2 sm:col-span-1">
                      <PMField
                        name="hours"
                        label="Timer"
                        type="text"
                        {...formik}
                        disabled={formDisabled(formik.values.date)}
                        onChange={(e) => {
                          let inputValue = e.target.value;
                          
                          // Replace comma with dot for decimal separator
                          inputValue = inputValue.replace(',', '.');
                          
                          // Allow empty, numbers, and decimal point - just store the value, don't convert yet
                          if (inputValue === '' || inputValue === '.' || /^-?\d*\.?\d*$/.test(inputValue)) {
                            formik.setFieldValue("hours", inputValue);
                            // Update time field for immediate feedback, but keep the decimal input visible
                            if (inputValue && inputValue !== '.' && !isNaN(parseFloat(inputValue))) {
                              const decimalHours = parseFloat(inputValue);
                              if (decimalHours >= 0) {
                                formik.setFieldValue("time", decimalHours.toFixed(3));
                              }
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const inputValue = e.target.value.replace(',', '.');
                          if (inputValue && inputValue !== '.' && !isNaN(parseFloat(inputValue))) {
                            const decimalHours = parseFloat(inputValue);
                            if (decimalHours >= 0) {
                              // Convert decimal to hours and minutes when user leaves the field
                              const { hours, minutes } = decimalToHoursMinutes(decimalHours);
                              formik.setFieldValue("hours", hours.toString());
                              formik.setFieldValue("minutes", minutes.toString());
                              formik.setFieldValue("time", decimalHours.toFixed(3));
                            }
                          } else if (inputValue === '' || inputValue === '.') {
                            // If empty or just a dot, set to 0
                            formik.setFieldValue("hours", "0");
                            formik.setFieldValue("minutes", "0");
                            formik.setFieldValue("time", "0");
                          }
                        }}
                      />
                    </div>
                    <div className="col-span-1 sm:col-span-1">
                      <PMField
                        name="minutes"
                        label="Minutter"
                        type="text"
                        {...formik}
                        disabled={formDisabled(formik.values.date)}
                        onChange={(e) => {
                          let inputValue = e.target.value;
                          
                          // Replace comma with dot
                          inputValue = inputValue.replace(',', '.');
                          
                          // Allow empty, numbers, and decimal point
                          if (inputValue === '' || inputValue === '.' || /^-?\d*\.?\d*$/.test(inputValue)) {
                            formik.setFieldValue("minutes", inputValue);
                            
                            // If it's a valid number, update time field
                            if (inputValue && inputValue !== '.' && !isNaN(parseFloat(inputValue))) {
                              let value = parseFloat(inputValue);
                              // Ensure minutes stay within 0-59 range
                              if (value > 59) value = 59;
                              if (value < 0) value = 0;
                              formik.setFieldValue("minutes", Math.round(value).toString());
                              const decimalTime = hoursMinutesToDecimal(formik.values.hours || 0, Math.round(value));
                              formik.setFieldValue("time", decimalTime.toFixed(3));
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const inputValue = e.target.value.replace(',', '.');
                          if (inputValue && inputValue !== '.' && !isNaN(parseFloat(inputValue))) {
                            let value = parseFloat(inputValue);
                            // Ensure minutes stay within 0-59 range
                            if (value > 59) value = 59;
                            if (value < 0) value = 0;
                            formik.setFieldValue("minutes", Math.round(value).toString());
                            const decimalTime = hoursMinutesToDecimal(formik.values.hours || 0, Math.round(value));
                            formik.setFieldValue("time", decimalTime.toFixed(3));
                          } else if (inputValue === '' || inputValue === '.') {
                            // If empty or just a dot, set to 0
                            formik.setFieldValue("minutes", "0");
                            const decimalTime = hoursMinutesToDecimal(formik.values.hours || 0, 0);
                            formik.setFieldValue("time", decimalTime.toFixed(3));
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="col-span-3 sm:col-span-1">
                    <PMField
                      name="time"
                      label="Tid"
                      type="number"
                      step="0.001"
                      min="0"
                      {...formik}
                      disabled={formDisabled(formik.values.date)}
                    />
                  </div>
                )}
                {/* Hidden time field for backend (always present) */}
                <input type="hidden" name="time" value={formik.values.time} />

                {renderAfterDateTime && (
                  <div className="col-span-3 sm:col-span-3">{renderAfterDateTime()}</div>
                )}

                {/* Show Report selector when editing existing hour with report */}
                {item?.id && item?.report && (
                  <div className="col-span-3 sm:col-span-3">
                    <ReportSelector
                      selected={item?.report}
                      name="report_id"
                      label="Rapport"
                      {...formik}
                      disabled={formDisabled(formik.values.date)}
                    />
                  </div>
                )}

                {/* Project: hidden when preset from WO/report/dashboard prefill unless hours admin */}
                {showProjectSelectorUi && (
                  <div className="col-span-3 sm:col-span-3">
                    <ProjectSelector
                      name="project_id"
                      label="Prosjekt"
                      selected={getCurrentProject()}
                      setFieldValue={formik.setFieldValue}
                      values={formik.values}
                      errors={formik.errors}
                      disabled={formDisabled(formik.values.date)}
                      onChange={(e) => {
                        if (e.target.value) {
                          formik.setFieldValue("project", e.target.object ?? null);
                          formik.setFieldValue("project_activity_id", null);
                          formik.setFieldValue("project_activity", null);
                          setItem((prev) => ({ ...prev, project: e.target.object }));
                        } else {
                          formik.setFieldValue("project", null);
                          formik.setFieldValue("project_activity_id", null);
                          formik.setFieldValue("project_activity", null);
                          setItem((prev) => ({ ...prev, project: null }));
                        }
                      }}
                    />
                  </div>
                )}

                {showProjectActivityUi && (
                <div className="col-span-3 sm:col-span-3">
                  <ModernProjectActivitySelectorField
                    name="project_activity_id"
                    label={t("project_activity.label")}
                    selected={getCurrentProjectActivity()}
                    project={
                      getCurrentProject() ||
                      (formik.values?.project_id ? { id: formik.values.project_id } : null)
                    }
                    setFieldValue={formik.setFieldValue}
                    errors={formik.errors}
                    disabled={formDisabled(formik.values.date)}
                    onChange={(e) => {
                      if (e.target.value) {
                        setItem((prev) => ({ ...prev, project_activity: e.target.object }));
                      } else {
                        setItem((prev) => ({ ...prev, project_activity: null }));
                      }
                    }}
                  />
                </div>
                )}

                <div className="col-span-3 sm:col-span-3">
                  <PMField
                    name="description"
                    label="Beskrivelse"
                    type="text"
                    multiline={true}
                    {...formik}
                    disabled={formDisabled(formik.values.date)}
                  />
                </div>

                {registrationPolicy?.time_entry_documents_enabled ? (
                  <div className="col-span-3 sm:col-span-3 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{t("hour.time_documents.title")}</p>
                    <p className="mt-1 text-xs text-slate-600">{t("hour.time_documents.subtitle")}</p>

                    {!resolvedId ? (
                      formDisabled(formik.values.date) ? (
                        <p className="mt-2 text-xs text-slate-500">{t("hour.time_documents.blocked_lock")}</p>
                      ) : hourLinkedToReport(formik.values.report_id) || formik.values.work_order_id ? (
                        <p className="mt-2 text-xs text-slate-500">{t("hour.time_documents.blocked_linked")}</p>
                      ) : (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs text-slate-600">{t("hour.time_documents.pending_hint")}</p>
                          <input
                            ref={pendingHourDocInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            accept={pendingDocAcceptAttr}
                            disabled={formDisabled(formik.values.date)}
                            onChange={(e) => {
                              const list = e.target.files;
                              if (!list?.length) return;
                              const added = [];
                              for (const file of Array.from(list)) {
                                if (!isHourPendingDocMimeAllowed(file)) {
                                  toast.error(t("hour.time_documents.pending_file_type_denied"));
                                  continue;
                                }
                                const key = `${file.name}_${file.size}_${file.lastModified}`;
                                added.push({ key, file });
                              }
                              if (added.length > 0) {
                                setPendingHourDocuments((prev) => [...prev, ...added]);
                              }
                              e.target.value = "";
                            }}
                          />
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              className="inline-flex rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={formDisabled(formik.values.date)}
                              onClick={() => pendingHourDocInputRef.current?.click()}
                            >
                              {t("hour.time_documents.pending_add")}
                            </button>
                            {pendingHourDocuments.length > 0 ? (
                              <span className="text-xs text-slate-500">
                                {t("hour.time_documents.pending_count", {
                                  count: pendingHourDocuments.length,
                                })}
                              </span>
                            ) : null}
                          </div>
                          {pendingHourDocuments.length > 0 ? (
                            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white text-xs">
                              {pendingHourDocuments.map((row) => (
                                <li
                                  key={row.key}
                                  className="flex items-center justify-between gap-2 px-2 py-1.5"
                                >
                                  <span className="min-w-0 truncate text-slate-800" title={row.file?.name}>
                                    {row.file?.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="shrink-0 text-slate-500 hover:text-rose-600"
                                    onClick={() =>
                                      setPendingHourDocuments((prev) =>
                                        prev.filter((x) => x.key !== row.key)
                                      )
                                    }
                                  >
                                    {t("hour.time_documents.pending_remove")}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      )
                    ) : formDisabled(formik.values.date) ? (
                      <p className="mt-2 text-xs text-slate-500">{t("hour.time_documents.blocked_lock")}</p>
                    ) : hourLinkedToReport(formik.values.report_id) || formik.values.work_order_id ? (
                      <p className="mt-2 text-xs text-slate-500">{t("hour.time_documents.blocked_linked")}</p>
                    ) : (
                      <div className="mt-3">
                        <DocumentManagerV6
                          module="hour"
                          moduleParam={String(resolvedId)}
                          mode="compact"
                          allowCopy={false}
                          allowMove={false}
                          disabled={formDisabled(formik.values.date)}
                          title={t("hour.time_documents.title")}
                        />
                      </div>
                    )}
                  </div>
                ) : null}

                {renderAfterDescription && (
                  <div className="col-span-3 sm:col-span-3">{renderAfterDescription()}</div>
                )}

                {formik.values?.user_id && formik.values?.date && canCarAllowance && hasTripsAvailable && (
                  <>
                    <div className="col-span-3 sm:col-span-3">
                      <div className="flex">
                        <button
                          type="button"
                          className="inline-flex align-right justify-center rounded-md border border-transparent bg-blue-400 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                          onClick={() => setCarAllowanceDialogOpen(true)}
                        >
                          {t("Add Car Allowance")}
                        </button>

                        <div className="px-4 py-2">
                          {formik?.values?.trips ? formik?.values?.trips?.length : 0} Turer valgt
                        </div>
                      </div>
                    </div>

                    <CarAllowanceSelectionModal
                      hour={formik.values}
                      open={carAllowanceDialogOpen}
                      onClose={() => setCarAllowanceDialogOpen(false)}
                      onSelect={(tripIds) => formik.setFieldValue("trips", tripIds)}
                      selected={formik.values?.trips}
                    />
                  </>
                )}

                <div className="col-span-3 sm:col-span-3 flex justify-between">
                  <button
                    type="button"
                    className="inline-flex align-right justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                    onClick={() => onClose && onClose()}
                  >
                    {t("Close")}
                  </button>
                  <button
                    disabled={formDisabled(formik.values.date) || loading}
                    type="button"
                    className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSubmitClickInternal}
                  >
                    {loading ? "Laster..." : (item?.id ? t("Update") : t("Create"))}
                  </button>
                </div>
              </div>
            </Form>
            )}
            </>
          );
        }}
      </Formik>

      {canProcessHour && (
        <ApproveHourDialog
          open={approveDialogOpen}
          hour={item}
          onClose={() => setApproveDialogOpen(false)}
        />
      )}
    </>
  );
};

HourForm.propTypes = {
  projectRequired: PropTypes.bool,
  reportRequired: PropTypes.bool,
  deviationRequired: PropTypes.bool,
  activityRequired: PropTypes.bool,
};
